/**
 * Per-row badge for the fixed Comments column.
 *
 * States:
 *   - 0 comments → a dashed bubble on its own, nothing to count.
 *   - N comments, unread (any comment newer than this user's lastSeen) →
 *     filled pill, dotted bubble + the count.
 *   - N comments, all read → outlined pill, plain bubble + the count.
 *
 * Comments attach to the board ROW via `(trackerId, rowKey)` — works for both
 * free-form tracker items and briefs rows, and is entirely separate from the
 * in-page brief comments. The unread comparison uses a browser-local lastSeen
 * store (see `useTrackerCommentsLastSeen`) — Phase-2 will move it to the server.
 *
 * Count + newest-comment time come from ONE board-level summary query shared by
 * every row's badge (same query key → a single request), so a large board no
 * longer fires one request per row.
 */
import { useQuery } from '@tanstack/react-query';
import { MessageSquareDashedIcon, MessageSquareDotIcon, MessageSquareIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@monksflow/monks-ui';

import { trackerCommentSummaryQueryOptions } from '@/entities/tracker-comment';

import { rowSeenKey, useTrackerCommentsLastSeen } from '../../lib/useTrackerCommentsLastSeen';

/** Above this the pill would outgrow its column, so the count saturates. */
const COUNT_CEILING = 99;

interface CommentsBadgeProps {
  trackerId: string;
  rowKey: string;
  onClick: () => void;
  /** Whether the comments panel is currently showing THIS row. */
  isPanelOpen?: boolean;
}

export function CommentsBadge({
  trackerId,
  rowKey,
  onClick,
  isPanelOpen = false,
}: CommentsBadgeProps) {
  const { t } = useTranslation('trackers');

  const { lastSeen } = useTrackerCommentsLastSeen();
  const entryQuery = useQuery({
    ...trackerCommentSummaryQueryOptions(trackerId),
    select: (summary) => summary[rowKey],
  });

  const total = entryQuery.data?.count ?? 0;
  const latestTimestamp = entryQuery.data?.latestAt ?? null;

  const seenAt = lastSeen[rowSeenKey(trackerId, rowKey)];
  const isUnread = total > 0 && (!seenAt || (latestTimestamp ? latestTimestamp > seenAt : false));

  const countLabel =
    total > COUNT_CEILING
      ? t('trackersBoard.commentsBadge.overflowCount', { count: COUNT_CEILING })
      : String(total);

  // One branch picks the glyph and the label together, so the icon can never
  // disagree with what assistive tech hears.
  const state =
    total === 0
      ? { Icon: MessageSquareDashedIcon, label: t('trackersBoard.commentsBadge.noComments') }
      : {
          Icon: isUnread ? MessageSquareDotIcon : MessageSquareIcon,
          label:
            t('trackersBoard.commentsBadge.commentCount', { count: total }) +
            (isUnread ? t('trackersBoard.commentsBadge.unreadSuffix') : ''),
        };

  return (
    <Button
      type="button"
      variant={isUnread ? 'default' : 'outline'}
      size={total === 0 ? 'icon-mini' : 'mini'}
      roundness="full"
      className={isPanelOpen ? 'border-foreground/40 bg-accent text-accent-foreground' : undefined}
      aria-label={state.label}
      title={state.label}
      aria-pressed={isPanelOpen}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <state.Icon className="size-3" aria-hidden />
      {total > 0 ? countLabel : null}
    </Button>
  );
}
